import { createTheme } from '@mui/material/styles'
import '@mui/material/styles'

import { colors } from '@/theme/colors'
import { typography } from '@/theme/typography'

const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const

const toImportant = (styles: Record<string, unknown>) => Object.fromEntries(Object.entries(styles).map(([k, v]) => [k, `${v} !important`]))

declare module '@mui/material/styles' {
  interface Theme {
    fontFamily: {
      primary: string
      secondary: string
    }
  }
  interface ThemeOptions {
    fontFamily?: {
      primary?: string
      secondary?: string
    }
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    action: true
  }
}

const darkTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536
    }
  },
  palette: {
    mode: 'dark',
    contrastThreshold: 4.5,
    primary: {
      light: colors.accent5,
      main: colors.accent5,
      contrastText: colors.contrast,
      dark: colors.accent1
    },
    secondary: {
      light: colors.accent4,
      main: colors.base,
      contrastText: colors.contrast,
      dark: colors.customAccent7
    },
    text: {
      primary: colors.contrast,
      secondary: colors.accent4
    },
    background: {
      default: colors.base
    },
    action: {
      hover: colors.accent2
    },
    divider: colors.accent3
  },
  zIndex: {
    appBar: 1300
  },
  typography: {
    fontFamily: ['"Inter"', 'Arial', 'sans-serif'].join(', '),
    fontSize: 16,
    h1: {
      width: '100%',
      ...typography.h1
    },
    h2: {
      width: '100%',
      ...typography.h2
    },
    h3: {
      width: '100%',
      ...typography.h3
    },
    h4: {
      width: '100%',
      ...typography.h4
    },
    h5: {
      width: '100%',
      ...typography.h5
    },
    h6: {
      width: '100%',
      ...typography.h6
    },
    button: {
      ...typography.h5,
      lineHeight: 1,
      letterSpacing: '1px',
      textTransform: 'uppercase'
    }
  },
  fontFamily: {
    primary: ['"Inter"', 'Arial', 'sans-serif'].join(', ')
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingBlock: theme.spacing(1.105),
          paddingInline: theme.spacing(5.9),
          [theme.breakpoints.up('md')]: {
            paddingBlock: theme.spacing(1.42),
            paddingInline: theme.spacing(6.25)
          },
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${theme.palette.background.default}, 0 0 0 4px ${theme.palette.text.primary} !important`,
            outline: '2px solid transparent !important'
          }
        })
      },
      variants: [
        {
          props: { variant: 'action' },
          style: ({ theme }) => ({
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.default,
            border: `2px solid ${theme.palette.text.primary}`,
            borderRadius: 0,
            whiteSpace: 'nowrap',
            '&:hover, &:focus-visible': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary
            },
            [theme.accessibility.highContrast.mediaQuery('active')]: {
              backgroundColor: theme.palette.background.default
            }
          })
        }
      ]
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${theme.palette.background.default}, 0 0 0 4px ${theme.palette.text.primary}`
          },
          [theme.accessibility.highContrast.mediaQuery('active')]: {
            color: 'currentColor !important',
            border: '2px solid ButtonText',
            '&:hover': {
              backgroundColor: 'Highlight',
              color: 'HighlightText',
              borderColor: 'HighlightText'
            }
          }
        })
      }
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover, &:focus, &:focus-visible': {
            color: `${theme.palette.primary.main} !important`
          }
        })
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          textDecoration: 'none !important',
          'label&': {
            textTransform: 'uppercase !important',
            alignSelf: 'flex-start',
            mb: 0,
            transition: 'color 0.3s',
            letterSpacing: '0.1px',
            fontStretch: 'condensed',
            fontSize: '0.875rem',
            color: `${colors.customAccent6} !important`,
            fontFamily: darkTheme.typography.button.fontFamily
          }
        }),
        caption: ({ theme }) => ({
          margin: '4px 0 4px 0 !important',
          padding: '0 !important',
          fontSize: '16px !important',
          fontFamily: `${theme.fontFamily.primary} !important`,
          fontStretch: 'condensed',
          color: `${theme.palette.text.primary} !important`,
          background: 'none !important',
          textShadow: 'none !important',
          cursor: 'default',
          textTransform: 'none !important'
        })
      }
    },
    MuiList: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5em'
          }
        })
      }
    },
    MuiSvgIcon: {
      variants: [
        {
          props: { fontSize: 'xlarge' },
          style: {
            fontSize: '4rem'
          }
        }
      ]
    },
    MuiTabs: {
      styleOverrides: {
        list: ({ theme }) => ({
          margin: '4px'
        })
      }
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          [theme.breakpoints.down('md')]: {
            marginRight: '0 !important'
          },
          '&:focus-visible': {
            border: `2px solid ${theme.palette.text.primary}`,
            boxShadow: `0 0 0 2px ${theme.palette.background.default}, 0 0 0 4px ${theme.palette.text.primary}`
          },
          '& span': {
            fontStretch: 'condensed !important',
            fontFamily: `${darkTheme.fontFamily.primary} !important`,
            textTransform: 'none !important',
            fontSize: '18px !important',
            letterSpacing: '0.1px !important',
            [theme.breakpoints.up('md')]: {
              fontSize: '22px !important'
            },
            [theme.breakpoints.down('md')]: {
              paddingInline: '0.25em !important'
            },
            '&:hover': {
              textDecoration: 'underline'
            }
          },
          '&:focus-visible span': {
            outline: `2px solid ${theme.palette.text.primary}`,
            outlineOffset: '0px'
          },
          '.kt-tab-inner-content-inner &': {
            [theme.breakpoints.down('md')]: {
              marginRight: '8px !important'
            }
          },
          '.kt-tab-inner-content-inner & .kt-tab-title': {
            borderStyle: 'solid'
          }
        })
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiInputBase-root': {
            fontFamily: `${theme.fontFamily.primary} !important`,
            fontStretch: 'condensed',
            borderRadius: '10px',
            paddingLeft: '0.5rem'
          },
          '& .Mui-focused fieldset': {
            borderWidth: '1px !important',
            borderColor: `${colors.accent2} !important`
          },
          '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${colors.accent2}`
            }
          }
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: `${colors.accent2} !important`,
          borderRadius: '0 !important'
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        'a:hover, a:focus-visible, button:hover, button:focus-visible': {
          textDecoration: 'underline !important'
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.text.primary} !important`,
          outlineOffset: '2px !important'
        },
        'input:focus-visible, textarea:focus-visible': {
          outline: 'none !important'
        },
        h1: theme.typography.h1,
        h2: theme.typography.h2,
        h3: theme.typography.h3,
        h4: theme.typography.h4,
        h5: theme.typography.h5,
        h6: theme.typography.h6,
        ...Object.fromEntries(
          headingTags.flatMap((tag) => [
            [tag, theme.typography[tag]],
            [`.MuiTypography-${tag}`, toImportant(theme.typography[tag])]
          ])
        ),
        [theme.breakpoints.down('sm')]: {
          'h1, .MuiTypography-h1': { fontSize: '2.8rem !important' },
          'h2, .MuiTypography-h2': { fontSize: '2.1rem !important' },
          'h3, .MuiTypography-h3': { fontSize: '1.5rem !important' },
          'h4, .MuiTypography-h4': { fontSize: '1.2rem !important' },
          'h5, .MuiTypography-h5': { fontSize: '1.1rem !important' },
          'h6, .MuiTypography-h6': { fontSize: '0.95rem !important' }
        }
      })
    }
  },
  accessibility: {
    highContrast: {
      mediaQuery: (key: string) => `@media (forced-colors: ${key})`
    },
    reduceMotion: { mediaQuery: () => `@media (prefers-reduced-motion)` }
  }
})

export default darkTheme
